'use client'

import React, { useEffect, useMemo, useState } from 'react'
import Image from 'next/image'
import { cn } from '@/utilities/ui'

export type Media = { url?: string; alt?: string } | null
export type Testimonial = {
  id: string
  name: string
  title?: string
  caption: string
  image?: Media
}

type Props = {
  testimonials: Testimonial[]
}

export default function TestimonialsRow({ testimonials }: Props) {
  const [activeSlide, setActiveSlide] = useState(0)

  const normalizedTestimonials = useMemo(() => {
    return testimonials.filter((t) => Boolean(t?.id && t?.caption)).slice(0)
  }, [testimonials])

  const visibleTestimonials = useMemo(() => {
    if (normalizedTestimonials.length === 0) return [] as Testimonial[]

    const windowedTestimonials: Testimonial[] = []
    for (let offset = 0; offset < 3; offset++) {
      const index = (activeSlide + offset) % normalizedTestimonials.length
      windowedTestimonials.push(normalizedTestimonials[index]!)
    }

    return windowedTestimonials
  }, [activeSlide, normalizedTestimonials])

  useEffect(() => {
    if (activeSlide >= normalizedTestimonials.length) {
      setActiveSlide(0)
    }
  }, [activeSlide, normalizedTestimonials.length])

  useEffect(() => {
    if (normalizedTestimonials.length <= 1) return

    const timer = window.setInterval(() => {
      setActiveSlide((current) => (current + 1) % normalizedTestimonials.length)
    }, 6000)

    return () => window.clearInterval(timer)
  }, [normalizedTestimonials.length])

  if (normalizedTestimonials.length === 0) return null

  return (
    <div>
      <div className="relative min-h-[1px]">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-6">
          {visibleTestimonials.map((t, visibleIndex) => {
            const img = t.image || null
            const imgUrl =
              img && typeof img === 'object' && 'url' in img ? (img.url as string) : undefined

            return (
              <div
                key={`${t.id}-${visibleIndex}`}
                className={cn(
                  'h-full rounded-2xl px-4 sm:px-6 py-3 sm:py-4',
                  'bg-white/10 border-2 border-[#029C90]/80',
                  'flex flex-col',
                )}
              >
                <div className="flex-1">
                  <p className="text-[13px] sm:text-[16px] text-white leading-snug sm:leading-relaxed not-italic">
                    "{t.caption}"
                  </p>
                </div>
                <div className="mt-4 sm:mt-6 flex items-center justify-between gap-2 sm:gap-3 pt-1 sm:pt-2">
                  {imgUrl ? (
                    <Image
                      src={imgUrl}
                      alt={(img as any)?.alt || t.name}
                      width={64}
                      height={64}
                      className="rounded-full object-cover w-12 h-12 sm:w-16 sm:h-16"
                    />
                  ) : (
                    <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-muted" />
                  )}
                  <div className="ml-1 sm:ml-2 flex-1 flex flex-col items-end text-right">
                    <span className="text-[13px] sm:text-[15px] font-semibold tracking-wide sm:tracking-wider uppercase text-white">
                      {t.name}
                    </span>
                    {t.title ? (
                      <span className="text-[11px] sm:text-[13px] text-muted-foreground">
                        {t.title}
                      </span>
                    ) : null}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {normalizedTestimonials.length > 1 ? (
        <div
          className="mt-6 flex items-center justify-center gap-2"
          aria-label="Testimonials carousel pagination"
        >
          {normalizedTestimonials.map((_, slideIndex) => (
            <button
              key={`dot-${slideIndex}`}
              type="button"
              aria-label={`Go to testimonial slide ${slideIndex + 1}`}
              aria-current={slideIndex === activeSlide ? 'true' : 'false'}
              onClick={() => setActiveSlide(slideIndex)}
              className={cn(
                'h-2.5 w-2.5 rounded-full transition-all',
                slideIndex === activeSlide ? 'bg-[#029C90] w-6' : 'bg-white/40 hover:bg-white/60',
              )}
            />
          ))}
        </div>
      ) : null}
    </div>
  )
}
